/**
 * BiblioSourcesDetailPage Component
 *
 * Full-page detail view for a single biblio source (journal article or any
 * other source type), identified by its numeric source (biblio) ID in the URL
 * path parameter `:biblioId`. Fetches via the type-agnostic getSourceById, so
 * it serves the /articles/:biblioId and /all-sources/:biblioId routes (the
 * default "articles" section) and the /reports/:biblioId route (the "reports"
 * section).
 *
 * Loads the source data, associated files, and displays all details. Article-
 * specific sections (topics, collections, extraction status, parasite rate) are
 * conditionally rendered, so non-article sources simply omit them. The
 * "reports" section swaps in the Reports sidebar, report wording, and a
 * "Back to reports" button, and hides topics and collections entirely.
 */

import { useEffect, useState, useCallback, useRef } from "react"
import { useParams, useNavigate, useLocation, Link } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible"
import {
  ArrowLeft,
  ChevronRight,
  Pencil,
  Loader2,
  FileX2,
  MapPin,
  BookOpen,
  Fingerprint,
  Globe2,
  FileText,
  Info,
  StickyNote,
  Files,
  Tags,
  ExternalLink,
} from "lucide-react"
import {
  JournalArticle,
  authorContactStatusLabelMap,
  exclusionReasonLabelMap,
  formatInstitutions,
  sourceTypeLabelMap,
  topicLabelMap,
  updateTopics,
} from "@/services/journalArticlesService"
import { getSourceById } from "@/services/allSourcesService"
import { getFilesForSource, GoogleDriveFile, addNote, isCollectionVisibleToUser, BiblioCollection } from "@/services/biblioService"
import ManageCollectionsDialog from "@/components/journal-articles/ManageCollectionsDialog"
import { getParasiteRateSourceIds } from "@/services/parasiteRateService"
import { useAuth } from "@/hooks/useAuth"
import { FileButton } from "@/components/shared/FileButton"
import UploadFileDialog from "@/components/journal-articles/UploadFileDialog"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import ExtractStatusBadge from "@/components/journal-articles/ExtractStatusBadge"
import UploadStatusBadge from "@/components/journal-articles/UploadStatusBadge"
import TopicsBadges from "@/components/journal-articles/TopicsBadges"
import parse from "html-react-parser"
import { formatDate } from "@/utils/dateUtils"
import JournalArticlesSidebar, { SidebarOption } from "@/components/journal-articles/JournalArticlesSidebar"
import { useJournalArticles } from "@/components/journal-articles/JournalArticlesContext"
import { sidebarOptionToPath } from "@/components/journal-articles/sidebarRoutes"
import ReportsSidebar from "@/components/reports/ReportsSidebar"
import { useReports } from "@/components/reports/ReportsContext"
import { ReportsSidebarOption, reportsSidebarOptionToPath } from "@/components/reports/reportsRoutes"
import { getReportTypeLabel } from "@/services/reportsService"

/** Page title for this view — imported by the route page to set document.title. */
export const PAGE_TITLE = "Source Detail"

const ALL_TOPICS = ["PR", "API", "IR", "Seasonality"] as const

/** The app section a detail page is shown in; decides sidebar, wording, and back target. */
type DetailPageSection = "articles" | "reports"

/** Section-specific settings for the detail page. */
interface SectionConfig {
  /** Label of the back button above the header. */
  backLabel: string
  /** Where the back button goes when there is no browser history to return to. */
  fallbackPath: string
  /** Singular noun used in the title fallback and the not-found state. */
  noun: string
  /** Error shown when the source fails to load and the API gave no message. */
  loadErrorMessage: string
  /** Whether the topics and collections sections and their dialogs are rendered. */
  showTopicsAndCollections: boolean
}

/**
 * Settings per section. Module-level (so each entry is a stable object
 * reference) and keyed by every DetailPageSection, so a new section without an
 * entry is a compile error.
 */
const SECTION_CONFIG: Record<DetailPageSection, SectionConfig> = {
  articles: {
    backLabel: "Back to articles",
    fallbackPath: "/articles",
    noun: "Article",
    loadErrorMessage: "Failed to load journal article",
    showTopicsAndCollections: true,
  },
  reports: {
    backLabel: "Back to reports",
    fallbackPath: "/reports",
    noun: "Report",
    loadErrorMessage: "Failed to load report",
    showTopicsAndCollections: false,
  },
}

/** Props for BiblioSourcesDetailPage. */
interface BiblioSourcesDetailPageProps {
  /**
   * Which section the page belongs to. "articles" (the default) serves
   * /articles/:biblioId and /all-sources/:biblioId; "reports" serves
   * /reports/:biblioId.
   */
  section?: DetailPageSection
}

/**
 * Full-page detail view for a single biblio source, identified by its numeric
 * biblio ID in the URL path parameter `:biblioId`.
 *
 * Loads the source data, associated Google Drive files, and displays all fields
 * with edit capabilities for notes and (in the articles section) topics and
 * collections.
 *
 * @param props - Component props.
 * @param props.section - "articles" (default) or "reports"; see BiblioSourcesDetailPageProps.
 * @returns The biblio source detail page component.
 */
export default function BiblioSourcesDetailPage({
  section = "articles",
}: BiblioSourcesDetailPageProps): React.JSX.Element {
  const { biblioId } = useParams<{ biblioId: string }>()
  const { getAccessToken, user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const sectionConfig: SectionConfig = SECTION_CONFIG[section]

  const { collections, collectionsLoading, sidebarOption, updateArticleInList } =
    useJournalArticles()

  /** Whether the "Manage collections" dialog is open. */
  const [manageCollectionsOpen, setManageCollectionsOpen] = useState<boolean>(false)

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false)

  const [createCollectionSignal, setCreateCollectionSignal] = useState<number>(0)

  const handleRequestCreateCollection = useCallback((): void => {
    setSidebarOpen(true)
    setCreateCollectionSignal((prev) => prev + 1)
  }, [])

  /**
   * Navigate to the articles list for the chosen sidebar option. Lets the user
   * jump straight to another view from the (collapsed) sidebar on this page.
   *
   * @param option - The sidebar option the user selected.
   */
  const handleSidebarOptionChange = useCallback(
    (option: SidebarOption): void => {
      navigate(sidebarOptionToPath(option))
    },
    [navigate]
  )

  /**
   * If the user navigated here from the articles table side panel, the article
   * object is passed via route state to avoid re-fetching.
   */
  const passedArticle = (location.state as { article?: JournalArticle } | null)?.article ?? null

  const [article, setArticle] = useState<JournalArticle | null>(passedArticle)
  const [loading, setLoading] = useState<boolean>(!passedArticle)
  const [error, setError] = useState<string | null>(null)

  // Source files from Google Drive
  const [sourceFiles, setSourceFiles] = useState<GoogleDriveFile[]>([])
  /** Whether source files are currently being fetched from the API. */
  const [filesLoading, setFilesLoading] = useState<boolean>(true)

  // Distinct source_id1 values that have parasite rate data (latest version),
  // used to decide whether to show the parasite rate map link for this article.
  const [parasiteRateSourceIds, setParasiteRateSourceIds] = useState<Set<string>>(new Set())

  // Notes state
  const [noteText, setNoteText] = useState<string>("")
  const [noteSubmitting, setNoteSubmitting] = useState<boolean>(false)
  const [localNotes, setLocalNotes] = useState(passedArticle?.notes ?? [])

  // Topic editing state
  const [editingTopics, setEditingTopics] = useState<boolean>(false)
  const [topicEdits, setTopicEdits] = useState<Record<string, boolean | null>>({})
  const [topicSubmitting, setTopicSubmitting] = useState<boolean>(false)
  const [localTopics, setLocalTopics] = useState({
    PR: passedArticle?.coversPRTopic ?? null,
    API: passedArticle?.coversAPITopic ?? null,
    IR: passedArticle?.coversIRTopic ?? null,
    Seasonality: passedArticle?.coversSeasonalityTopic ?? null,
  })

  const currentTopicState: Record<string, boolean | null> = localTopics

  /**
   * Navigate back to the list. Uses navigate(-1) so the cached list state
   * (pagination, filters, scroll) is preserved when the user arrived from the
   * list. Falls back to the section's list (/articles or /reports) when there
   * is no history to go back to (e.g. the page was opened via a shared link).
   */
  const handleGoBack = useCallback((): void => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate(sectionConfig.fallbackPath)
    }
  }, [navigate, sectionConfig])

  /** Stable ref to fetchArticle so callback doesn't create dependency loops. */
  const fetchArticleRef = useRef<() => Promise<void>>(() => Promise.resolve())

  /**
   * Fetches the journal article from the API by sourceId.
   * Only called when the article wasn't passed via route state, or when refreshing.
   */
  const fetchArticle = useCallback(async (): Promise<void> => {
    if (!biblioId) {
      setError("No biblio ID provided")
      setLoading(false)
      return
    }

    const parsedId = parseInt(biblioId, 10)
    if (isNaN(parsedId)) {
      setError(`Invalid biblio ID: ${biblioId}`)
      setLoading(false)
      return
    }

    try {
      const token = await getAccessToken()
      const found = await getSourceById(token, parsedId)
      setArticle(found)
      setLocalNotes(found.notes ?? [])
      setLocalTopics({
        PR: found.coversPRTopic ?? null,
        API: found.coversAPITopic ?? null,
        IR: found.coversIRTopic ?? null,
        Seasonality: found.coversSeasonalityTopic ?? null,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : sectionConfig.loadErrorMessage)
    } finally {
      setLoading(false)
    }
  }, [biblioId, getAccessToken, sectionConfig])

  fetchArticleRef.current = fetchArticle

  // Only fetch from the API if no article was passed via route state
  useEffect(() => {
    if (!passedArticle) {
      fetchArticle()
    }
  }, [passedArticle, fetchArticle])

  useEffect(() => {
    async function fetchParasiteRateSourceIds(): Promise<void> {
      try {
        const token = await getAccessToken()
        const { sourceIds } = await getParasiteRateSourceIds(token)
        setParasiteRateSourceIds(sourceIds)
      } catch (err) {
        console.error("Failed to fetch parasite rate source IDs:", err)
      }
    }

    fetchParasiteRateSourceIds()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load source files when article is available
  useEffect(() => {
    if (!article) return

    let cancelled = false
    setFilesLoading(true)

    async function loadSourceFiles(): Promise<void> {
      try {
        const token = await getAccessToken()
        const response = await getFilesForSource(token, article!.sourceId)
        if (!cancelled && response.files) {
          setSourceFiles(response.files)
        }
      } catch (err) {
        console.error("Failed to load source files:", err)
      } finally {
        if (!cancelled) {
          setFilesLoading(false)
        }
      }
    }

    loadSourceFiles()
    return () => { cancelled = true }
  }, [article, getAccessToken])

  /**
   * Refresh the source files list from the API.
   * Called after a successful file upload to reflect the new file in the UI.
   */
  const refreshFiles = useCallback(async (): Promise<void> => {
    if (!article) return
    setFilesLoading(true)
    try {
      const token = await getAccessToken()
      const response = await getFilesForSource(token, article.sourceId)
      if (response.files) {
        setSourceFiles(response.files)
      }
    } catch (err) {
      console.error("Failed to refresh source files:", err)
    } finally {
      setFilesLoading(false)
    }
  }, [article, getAccessToken])

  /**
   * Submit a new note via the API and update local state on success.
   */
  const handleAddNote = useCallback(async (): Promise<void> => {
    if (!noteText.trim() || noteSubmitting || !article) return
    setNoteSubmitting(true)
    try {
      const token = await getAccessToken()
      const createdBy: string = user?.email ?? user?.name ?? "Unknown"
      const response = await addNote(token, article.sourceId, noteText.trim(), createdBy)
      setLocalNotes((prev) => [response.note, ...prev])
      setNoteText("")
    } catch (err) {
      console.error("Failed to add note:", err)
    } finally {
      setNoteSubmitting(false)
    }
  }, [noteText, noteSubmitting, article, getAccessToken, user])

  /**
   * Enter topic editing mode, initialising edits from the current state.
   */
  const startEditingTopics = (): void => {
    setTopicEdits({ ...currentTopicState })
    setEditingTopics(true)
  }

  /**
   * Toggle a topic value in the edit state.
   * If the topic was originally unset (null), cycles: null -> true -> false -> null.
   * If already set, only toggles between true and false.
   */
  const toggleTopicEdit = (topic: string): void => {
    setTopicEdits((prev) => {
      const current = prev[topic]
      const original = currentTopicState[topic]
      let next: boolean | null
      if (original === null || original === undefined) {
        if (current === null || current === undefined) next = true
        else if (current === true) next = false
        else next = null
      } else {
        next = !current
      }
      return { ...prev, [topic]: next }
    })
  }

  /**
   * Submit topic edits via the API. Only sends topics that actually changed.
   */
  const handleSaveTopics = useCallback(async (): Promise<void> => {
    if (topicSubmitting || !article) return
    const changed: Record<string, boolean> = {}
    for (const topic of ALL_TOPICS) {
      const edited = topicEdits[topic]
      const original = currentTopicState[topic]
      if (edited !== original && edited !== null) {
        changed[topic] = edited
      }
    }
    if (Object.keys(changed).length === 0) {
      setEditingTopics(false)
      return
    }
    setTopicSubmitting(true)
    try {
      const token = await getAccessToken()
      const createdBy: string = user?.email ?? user?.name ?? "Unknown"
      await updateTopics(token, article.sourceId, changed, createdBy)
      setLocalTopics((prev) => {
        const updated = { ...prev }
        for (const [key, value] of Object.entries(changed)) {
          updated[key as keyof typeof updated] = value
        }
        return updated
      })
      setEditingTopics(false)
    } catch (err) {
      console.error("Failed to update topics:", err)
    } finally {
      setTopicSubmitting(false)
    }
  }, [topicSubmitting, article, topicEdits, currentTopicState, getAccessToken, user])

  /** Map of collectionId to collectionName for display. */
  const collectionNameMap = new Map<number, string>(
    collections.map((c) => [c.id, c.collectionName])
  )
  /** Map of collectionId to the full collection record, for membership checks. */
  const collectionById = new Map<number, BiblioCollection>(
    collections.map((c) => [c.id, c])
  )

  /**
   * Handle a successful collection-membership change: apply the added and
   * removed collection ids to the locally-held article so the pills update
   * immediately, and sync the cached articles list so the table reflects the
   * change too.
   *
   * @param addedCollectionIds - Collection ids the article was added to.
   * @param removedCollectionIds - Collection ids the article was removed from.
   */
  const handleCollectionsChanged = (
    addedCollectionIds: number[],
    removedCollectionIds: number[]
  ): void => {
    const removedSet = new Set<number>(removedCollectionIds)
    setArticle((prev) => {
      if (prev === null) {
        return prev
      }
      const kept = (prev.collectionIds ?? []).filter((id) => !removedSet.has(id))
      const additions = addedCollectionIds.filter((id) => !kept.includes(id))
      const updated: JournalArticle = {
        ...prev,
        collectionIds: [...kept, ...additions],
      }
      // Keep the shared articles cache in sync so the list/table pills match.
      updateArticleInList(updated)
      return updated
    })
  }

  // Loading state
  if (loading) {
    return (
      <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
        <CardContent className="flex flex-1 flex-col min-h-0 items-center justify-center">
          <LoadingSpinner />
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (error || !article) {
    return (
      <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={handleGoBack}>
              <ArrowLeft className="size-4" />
            </Button>
            <CardTitle><h1>{sectionConfig.noun} Not Found</h1></CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col min-h-0">
          <p className="text-muted-foreground">
            {error ?? `The requested ${sectionConfig.noun.toLowerCase()} could not be found.`}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <title>{PAGE_TITLE} - MAP Apps Platform</title>
      <div className="flex flex-1 min-h-0">
        {section === "reports" ? (
          <ReportsDetailSidebar
            isOpen={sidebarOpen}
            onToggle={() => setSidebarOpen(prev => !prev)}
          />
        ) : (
          <JournalArticlesSidebar
            selectedOption={sidebarOption}
            onOptionChange={handleSidebarOptionChange}
            collections={collections}
            collectionsLoading={collectionsLoading}
            isOpen={sidebarOpen}
            onToggle={() => setSidebarOpen(prev => !prev)}
            openNewCollectionSignal={createCollectionSignal}
          />
        )}
        <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none pt-0">
          <Button variant="ghost" className="size-7 w-fit mt-1 ml-2.5 mb-1" onClick={handleGoBack}>
            <ArrowLeft />
            {sectionConfig.backLabel}
          </Button>
          <CardHeader className="gap-4 border-b border-t bg-muted/30 -mt-6 pt-2 pb-0!">
            <div className="min-w-0 space-y-3">
              <CardTitle>
                <h1 className="text-2xl font-bold leading-tight tracking-tight text-foreground md:text-3xl max-w-6xl ">
                  {article.title || `Untitled ${sectionConfig.noun}`}
                </h1>
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {article.journaltitle && (
                  <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                    <BookOpen className="size-4 text-primary" />
                    {article.journaltitle}
                  </span>
                )}
                {article.publicationYear && (
                  <Badge variant="secondary" className="font-normal">
                    {article.publicationYear}
                  </Badge>
                )}
                {article.sourceType && (
                  <Badge variant="outline" className="font-normal">
                    {sourceTypeLabelMap[article.sourceType] ?? article.sourceType}
                  </Badge>
                )}
              </div>
              <CardDescription className="text-xs font-semibold uppercase tracking-wider">
                Source ID: {article.sourceId}
              </CardDescription>

              {sectionConfig.showTopicsAndCollections && (
              <div className="mt-1 flex flex-wrap gap-x-8 gap-y-3 border-t pt-3">
                <div className="flex items-start gap-2">
                  <span className="shrink-0 pt-0.5 text-xs font-bold uppercase tracking-wider text-foreground">
                    Topics
                  </span>
                  <div className="text-sm text-foreground">
                    <TopicsSummary topics={localTopics} />
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="shrink-0 pt-0.5 text-xs font-bold uppercase tracking-wider text-foreground">
                    Collections
                  </span>
                  <div className="text-sm text-foreground">
                    {(() => {
                      const visibleCollectionIds: number[] = (article.collectionIds ?? []).filter(
                        id => {
                          const collection = collectionById.get(id)
                          if (collection === undefined) {
                            return false
                          }
                          return isCollectionVisibleToUser(collection, user?.sub)
                        }
                      )

                      return visibleCollectionIds.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {visibleCollectionIds.map((id, idx) => (
                            <Badge key={idx} variant="outline">
                              {collectionNameMap.get(id) ?? id}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )
                    })()}
                  </div>
                </div>
              </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col min-h-0 overflow-y-auto">
            <div className="space-y-6 max-w-5xl">
              {/* Files */}
              <section className="rounded-lg border bg-card shadow-sm">
                <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-primary" aria-hidden="true">
                      <Files className="size-4" />
                    </span>
                    <h2 className="text-sm font-semibold text-foreground">Files</h2>
                  </div>
                  <UploadFileDialog sourceId={article.sourceId} onUploadComplete={refreshFiles} />
                </div>
                <div className="p-4">
                  {filesLoading ? (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Loader2 className="size-3.5 animate-spin" />
                      <span>Loading files…</span>
                    </div>
                  ) : sourceFiles.length > 0 ? (
                    <div className="flex gap-2 flex-wrap">
                      {sourceFiles.map(file => (
                        <FileButton key={file.id} file={file} />
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <FileX2 className="size-3.5" />
                      <span>No files found for this source</span>
                    </div>
                  )}
                </div>
              </section>

              {/* Publication Info — authors lead the card, followed by the field grid. */}
              <DetailCard
                icon={<BookOpen className="size-4" />}
                title="Publication"
                className="space-y-4"
              >
                {article.authors?.length > 0 && (
                  <DetailSection label="Authors">
                    {article.authors.map(a => a.name).join("; ")}
                  </DetailSection>
                )}
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <DetailSection label="Journal">{article.journaltitle || "—"}</DetailSection>
                  <DetailSection label="Year">{article.publicationYear || "—"}</DetailSection>
                  <DetailSection label="Volume">{article.volume || "—"}</DetailSection>
                  <DetailSection label="Issue">{article.issue || "—"}</DetailSection>
                  <DetailSection label="Pages">{article.pages || "—"}</DetailSection>
                  <DetailSection label="Source Type">
                    {article.sourceType
                      ? (sourceTypeLabelMap[article.sourceType] ?? article.sourceType)
                      : "—"}
                  </DetailSection>
                </div>
              </DetailCard>

              {/* Identifiers */}
              <DetailCard
                icon={<Fingerprint className="size-4" />}
                title="Identifiers"
                className="grid grid-cols-2 gap-4 sm:grid-cols-3"
              >
                <DetailSection label="DOI">
                  {article.doi ? (
                    <a
                      href={article.doiUrl || `https://doi.org/${article.doi}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="break-all text-primary hover:underline"
                    >
                      {article.doi}
                    </a>
                  ) : (
                    "—"
                  )}
                </DetailSection>
                <DetailSection label="PubMed ID">
                  {article.pubmedId ? (
                    <a
                      href={
                        article.pubmedUrl || `https://pubmed.ncbi.nlm.nih.gov/${article.pubmedId}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {article.pubmedId}
                    </a>
                  ) : (
                    "—"
                  )}
                </DetailSection>
                <DetailSection label="IHME NID">{article.ihmeNid || "—"}</DetailSection>
                <DetailSection label="MAP Survey ID">
                  {article.mapSurveyId ? (
                    // Opens the Survey Catalogue entry for this survey in a new tab
                    // (same link styling as PointDetailCard's survey links).
                    <Link
                      to={`/survey-catalogue/${encodeURIComponent(article.mapSurveyId)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline underline-offset-2 inline-flex items-center gap-1 hover:text-primary/80"
                    >
                      {article.mapSurveyId}
                      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                    </Link>
                  ) : (
                    "—"
                  )}
                </DetailSection>
                {article.sourceType === "report" && (
                  // Read-only; getReportTypeLabel returns "Needs Review" when unset
                  // and throws on a value missing from reportTypeLabelMap.
                  <DetailSection label="Report Type">
                    {getReportTypeLabel(article.reportType)}
                  </DetailSection>
                )}
              </DetailCard>

              {article.abstract && (
                <DetailCard icon={<FileText className="size-4" />} title="Abstract">
                  <div className="rounded-r-lg border-l-4 border-ring bg-slate-50 p-4 text-sm leading-relaxed whitespace-pre-wrap dark:border-muted-foreground/40 dark:bg-muted/40">
                    {parse(article.abstract)}
                  </div>
                </DetailCard>
              )}

              {/* Citations & URL */}
              {(article.citation || article.suggestedCitation || article.url) && (
                <DetailCard
                  icon={<FileText className="size-4" />}
                  title="Citation & Links"
                  className="space-y-4"
                >
                  {article.citation && (
                    <DetailSection label="Citation">
                      <p className="text-sm italic">{article.citation}</p>
                    </DetailSection>
                  )}
                  {article.suggestedCitation && (
                    <DetailSection label="Suggested Citation">
                      <p className="text-sm italic">{article.suggestedCitation}</p>
                    </DetailSection>
                  )}
                  {article.url && (
                    <DetailSection label="URL">
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-sm text-primary hover:underline"
                      >
                        {article.url}
                      </a>
                    </DetailSection>
                  )}
                </DetailCard>
              )}

              {/* Metadata */}
              <DetailCard
                icon={<Info className="size-4" />}
                title="Metadata"
                className="space-y-5"
              >
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <DetailSection label="Survey Type">{article.surveyType || "—"}</DetailSection>
                  <DetailSection label="Usage Permission">
                    {article.usagePermission || "—"}
                  </DetailSection>
                  <DetailSection label="Created By">
                    <span className="break-all">{article.createdBy || "—"}</span>
                  </DetailSection>
                  <DetailSection label="Created Date">
                    {formatDate(article.createdDate)}
                  </DetailSection>
                  <DetailSection label="Last Modified By">
                    <span className="break-all">{article.lastModifiedBy || "—"}</span>
                  </DetailSection>
                  <DetailSection label="Last Modified Date">
                    {formatDate(article.lastModifiedDate)}
                  </DetailSection>
                </div>


                {sectionConfig.showTopicsAndCollections && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <DetailSection
                    label={
                      <span className="flex items-center gap-1">
                        Topics
                        <button
                          type="button"
                          onClick={startEditingTopics}
                          className="text-foreground/80 hover:text-foreground transition-colors"
                          aria-label="Edit topics"
                        >
                          <Pencil className="size-3.5" strokeWidth={2} />
                        </button>
                      </span>
                    }
                  >
                    <TopicsSummary topics={localTopics} />

                    {/* Topic History */}
                    {article.topicHistory?.length > 0 && (
                      <Collapsible>
                        <CollapsibleTrigger className="flex items-center mt-2 gap-1 text-xs font-medium text-muted-foreground hover:text-foreground cursor-pointer">
                          <ChevronRight className="size-3 transition-transform duration-200 [[data-state=open]>&]:rotate-90" />
                          Topic History ({article.topicHistory.length})
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="space-y-1.5 mt-1.5">
                            {[...article.topicHistory]
                              .sort((a, b) => b.createdDate.localeCompare(a.createdDate))
                              .map((entry, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center gap-2 text-xs text-muted-foreground"
                                >
                                  <Badge
                                    variant={entry.status ? "default" : "secondary"}
                                    className="text-[10px] px-1.5 py-0 shrink-0"
                                  >
                                    {entry.topic} {entry.status ? "\u2713" : "\u2717"}
                                  </Badge>
                                  <span className="truncate min-w-0">
                                {entry.createdBy === "system:comprehend-classifier"
                                  ? "AI Labelled"
                                  : entry.createdBy}
                              </span>
                                  <span className="ml-auto shrink-0">
                                {new Date(entry.createdDate).toLocaleDateString()}
                              </span>
                                </div>
                              ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </DetailSection>

                  <DetailSection
                    label={
                      <span className="flex items-center gap-1">
                        Collections
                        <button
                          type="button"
                          onClick={() => setManageCollectionsOpen(true)}
                          className="text-foreground/80 hover:text-foreground transition-colors"
                          aria-label="Manage this article's collections"
                        >
                          <Pencil className="size-3.5" strokeWidth={2} />
                        </button>
                      </span>
                    }
                  >
                    {(() => {
                      const visibleCollectionIds: number[] = (article.collectionIds ?? []).filter(
                        id => {
                          const collection = collectionById.get(id)
                          if (collection === undefined) {
                            return false
                          }
                          return isCollectionVisibleToUser(collection, user?.sub)
                        }
                      )

                      return visibleCollectionIds.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {visibleCollectionIds.map((id, idx) => (
                            <Badge key={idx} variant="outline">
                              {collectionNameMap.get(id) ?? id}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )
                    })()}
                  </DetailSection>
                </div>
                )}
              </DetailCard>

              {/* Research Notes */}
              {article.researchNotes && (
                <DetailCard icon={<FileText className="size-4" />} title="Research Notes">
                  <p className="text-sm whitespace-pre-wrap">{article.researchNotes}</p>
                </DetailCard>
              )}

              {/* Contacts / Institutions */}
              {/* formatInstitutions joins report lists and yields "" for an empty list, so it doubles as the presence check */}
              {(article.contacts || article.institution || formatInstitutions(article.institutions)) && (
                <DetailCard icon={<Info className="size-4" />} title="Contacts & Institutions">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {article.contacts && (
                      <DetailSection label="Contacts">{article.contacts}</DetailSection>
                    )}
                    {article.institution && (
                      <DetailSection label="Institution">{article.institution}</DetailSection>
                    )}
                    {formatInstitutions(article.institutions) && (
                      <DetailSection label="Institutions">{formatInstitutions(article.institutions)}</DetailSection>
                    )}
                  </div>
                </DetailCard>
              )}

              {/* Geographic & temporal coverage, grouped into one card. */}
              {((article.studyYears && article.studyYears.length > 0) ||
                (article.dateEntities && article.dateEntities.length > 0) ||
                (article.continents && article.continents.length > 0) ||
                (article.countries && article.countries.length > 0) ||
                (article.locationEntities && article.locationEntities.length > 0)) && (
                <DetailCard
                  icon={<Globe2 className="size-4" />}
                  title="Geographic & Temporal Coverage"
                  className="space-y-4"
                >
                  {article.studyYears && article.studyYears.length > 0 && (
                    <DetailSection label="Study Years">
                      <div className="flex flex-wrap gap-1">
                        {article.studyYears.map((year, idx) => (
                          <Badge key={idx} variant="outline">
                            {year}
                          </Badge>
                        ))}
                      </div>
                    </DetailSection>
                  )}

                  {article.dateEntities && article.dateEntities.length > 0 && (
                    <DetailSection label="Date Entities">
                      <div className="flex flex-wrap gap-1">
                        {article.dateEntities.map((date, idx) => (
                          <Badge key={idx} variant="outline">
                            {date}
                          </Badge>
                        ))}
                      </div>
                    </DetailSection>
                  )}

                  {article.continents && article.continents.length > 0 && (
                    <DetailSection label="Continents">
                      <div className="flex flex-wrap gap-1">
                        {article.continents.map((continent, idx) => (
                          <Badge key={idx} variant="outline">
                            {continent}
                          </Badge>
                        ))}
                      </div>
                    </DetailSection>
                  )}

                  {article.countries && article.countries.length > 0 && (
                    <DetailSection label="Countries">
                      <div className="flex flex-wrap gap-1">
                        {article.countries.map((country, idx) => (
                          <Badge key={idx} variant="outline">
                            {country}
                          </Badge>
                        ))}
                      </div>
                    </DetailSection>
                  )}

                  {article.locationEntities && article.locationEntities.length > 0 && (
                    <DetailSection label="Locations">
                      <div className="flex flex-wrap gap-1">
                        {article.locationEntities.map((location, idx) => (
                          <Badge key={idx} variant="outline">
                            {location}
                          </Badge>
                        ))}
                      </div>
                    </DetailSection>
                  )}
                </DetailCard>
              )}

              {/* PR Extraction Status */}
              {(article.prExtractionStatus ||
                article.prUploadStatus ||
                article.prAuthorContactStatus ||
                article.prExclusionReason) && (
                <DetailCard icon={<Tags className="size-4" />} title="PR Extraction Status">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {article.prExtractionStatus != null && (
                      <DetailSection label="Extraction Status">
                        <ExtractStatusBadge status={article.prExtractionStatus} />
                      </DetailSection>
                    )}
                    {article.prUploadStatus != null && (
                      <DetailSection label="Upload Status">
                        <UploadStatusBadge status={article.prUploadStatus} />
                      </DetailSection>
                    )}
                    {article.prAuthorContactStatus != null && (
                      <DetailSection label="Author Contact Status">
                        {authorContactStatusLabelMap[article.prAuthorContactStatus] ??
                          article.prAuthorContactStatus}
                      </DetailSection>
                    )}
                    {article.prExclusionReason != null && (
                      <DetailSection label="Exclusion Reason">
                        {exclusionReasonLabelMap[article.prExclusionReason] ??
                          article.prExclusionReason}
                      </DetailSection>
                    )}
                    {article.prExtractionFileId != null && (
                      <DetailSection label="Extraction File">
                        <a
                          href={`https://docs.google.com/spreadsheets/d/${article.prExtractionFileId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Extraction Sheet
                        </a>
                      </DetailSection>
                    )}
                  </div>
                </DetailCard>
              )}

              {/* RS Extraction Status */}
              {(article.rsExtractionStatus || article.rsUploadStatus) && (
                <DetailCard icon={<Tags className="size-4" />} title="RS Extraction Status">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {article.rsExtractionStatus != null && (
                      <DetailSection label="Extraction Status">
                        <ExtractStatusBadge status={article.rsExtractionStatus} />
                      </DetailSection>
                    )}
                    {article.rsUploadStatus != null && (
                      <DetailSection label="Upload Status">
                        <UploadStatusBadge status={article.rsUploadStatus} />
                      </DetailSection>
                    )}
                    {article.rsExtractionFileId != null && (
                      <DetailSection label="Extraction File">
                        <a
                          href={`https://docs.google.com/spreadsheets/d/${article.rsExtractionFileId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Extraction Sheet
                        </a>
                      </DetailSection>
                    )}
                  </div>
                </DetailCard>
              )}

              {parasiteRateSourceIds.has(String(article.sourceId)) && (
                <DetailCard icon={<MapPin className="size-4" />} title="Parasite Rate">
                  <Button variant="outline" size="sm" asChild className="w-fit">
                    <Link
                      to={`/data/parasite-rate/clusters?sources=${encodeURIComponent(String(article.sourceId))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`View source ${article.sourceId} on the parasite rate map (opens in a new tab)`}
                    >
                      <MapPin data-icon="inline-start" />
                      View on parasite rate map
                    </Link>
                  </Button>
                </DetailCard>
              )}

              {/* Notes */}
              <DetailCard
                icon={<StickyNote className="size-4" />}
                title="Notes"
                className="space-y-3"
              >
                {localNotes.length > 0 && (
                  <div className="space-y-2">
                    {[...localNotes]
                      .sort((a, b) => b.createdDate.localeCompare(a.createdDate))
                      .map((note, idx) => (
                        <div key={idx} className="rounded-md border bg-muted/20 p-3 text-sm">
                          <p className="whitespace-pre-wrap">{note.noteText}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <span className="truncate min-w-0">{note.createdBy}</span>
                            <span className="ml-auto shrink-0">
                              {new Date(note.createdDate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
                <Textarea
                  placeholder="Add a note..."
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  className="min-h-[80px] resize-none"
                />
                <Button
                  size="sm"
                  onClick={handleAddNote}
                  disabled={!noteText.trim() || noteSubmitting}
                >
                  {noteSubmitting ? "Adding..." : "Add Note"}
                </Button>
              </DetailCard>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Topics edit and manage-collections dialogs; only in sections that show topics and collections. */}
      {sectionConfig.showTopicsAndCollections && (
      <>
      {/* Topics edit dialog, driven by the Topics section pencil. */}
      <Dialog
        open={editingTopics}
        onOpenChange={nextOpen => {
          // Block closing (overlay/escape) while a save is in flight so the
          // request cannot be abandoned mid-flight.
          if (!nextOpen && !topicSubmitting) {
            setEditingTopics(false)
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Topics</DialogTitle>
            <DialogDescription>
              Toggle which topics this article covers. Dashed topics are unset.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-1.5">
            {ALL_TOPICS.map(topic => {
              const value = topicEdits[topic]
              return (
                <button
                  key={topic}
                  type="button"
                  onClick={() => toggleTopicEdit(topic)}
                  className={`text-sm px-2.5 py-1 rounded-md border cursor-pointer transition-colors ${
                    value === true
                      ? "bg-primary text-primary-foreground border-primary"
                      : value === false
                        ? "bg-secondary text-secondary-foreground border-secondary line-through"
                        : "bg-background text-muted-foreground border-dashed"
                  }`}
                >
                  {topicLabelMap[topic.toLowerCase()] ?? topic}
                </button>
              )
            })}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingTopics(false)}
              disabled={topicSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveTopics} disabled={topicSubmitting}>
              {topicSubmitting && <Loader2 className="size-3.5 animate-spin" />}
              {topicSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage-collections dialog, driven by the Collections section button. */}
      <ManageCollectionsDialog
        open={manageCollectionsOpen}
        sourceId={article.sourceId}
        collections={collections}
        existingCollectionIds={article.collectionIds ?? []}
        onClose={() => setManageCollectionsOpen(false)}
        onChanged={result =>
          handleCollectionsChanged(result.addedCollectionIds, result.removedCollectionIds)
        }
        onRequestCreateCollection={handleRequestCreateCollection}
      />
      </>
      )}
    </>
  )
}

/**
 * The Reports sidebar as shown on the report detail page.
 *
 * A separate component so that only the "reports" section calls useReports():
 * the articles and All Sources detail routes then never depend on
 * ReportsProvider, and each hook is still called unconditionally (the page
 * picks which component to render, not which hook to call).
 *
 * The highlighted option is the list the user came from (the Reports context's
 * current sidebar option); clicking an option navigates to that list.
 *
 * @param props - Component props.
 * @param props.isOpen - Whether the sidebar is expanded (controlled by the page).
 * @param props.onToggle - Toggles the sidebar's expanded state.
 * @returns The Reports sidebar wired to the Reports context and router.
 */
function ReportsDetailSidebar({
  isOpen,
  onToggle,
}: {
  isOpen: boolean
  onToggle: () => void
}): React.JSX.Element {
  const navigate = useNavigate()
  const { sidebarOption } = useReports()

  /**
   * Navigate to the reports list for the chosen sidebar option.
   *
   * @param option - The sidebar option the user selected.
   */
  const handleOptionChange = useCallback(
    (option: ReportsSidebarOption): void => {
      navigate(reportsSidebarOptionToPath(option))
    },
    [navigate]
  )

  return (
    <ReportsSidebar
      selectedOption={sidebarOption}
      onOptionChange={handleOptionChange}
      isOpen={isOpen}
      onToggle={onToggle}
    />
  )
}

function TopicsSummary({
  topics,
}: {
  topics: Record<string, boolean | null>
}): React.JSX.Element {
  const covered: string[] = []
  const notCovered: string[] = []
  for (const [key, value] of Object.entries(topics)) {
    if (value === true) {
      covered.push(key.toLowerCase())
    } else if (value === false) {
      notCovered.push(key.toLowerCase())
    }
  }

  if (covered.length === 0 && notCovered.length === 0) {
    return <span className="text-muted-foreground">—</span>
  }

  return <TopicsBadges topics={covered} nonTopics={notCovered} />
}

/**
 * Helper component for rendering a labelled detail field.
 *
 * @param props - Component props.
 * @param props.label - The field label (string or ReactNode).
 * @param props.children - The field value content.
 */
function DetailSection({ label, children }: { label: React.ReactNode; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  )
}

/**
 * Helper component that groups related detail fields into a titled surface card.
 *
 * Replaces the previous flat `<Separator />`-delimited sections with visually
 * distinct panels, each carrying an icon and a heading, so the long detail page
 * reads as scannable groups rather than one continuous list.
 *
 * @param props - Component props.
 * @param props.icon - Icon node rendered beside the section title.
 * @param props.title - The section heading text.
 * @param props.children - The grouped field content.
 * @param props.className - Optional extra classes applied to the content wrapper.
 * @returns A bordered surface card wrapping the provided detail content.
 */
function DetailCard({
  icon,
  title,
  children,
  className = "",
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
  className?: string
}): React.JSX.Element {
  return (
    <section className="rounded-lg border bg-card shadow-sm">
      <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-2.5">
        <span className="text-primary" aria-hidden="true">
          {icon}
        </span>
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      </div>
      <div className={`p-4 ${className}`}>{children}</div>
    </section>
  )
}
